import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://learnsocialai.net';
const SITE_NAME = 'Learn Social AI';
const DEFAULT_IMAGE = `${SITE_URL}/logo192.png`;

type SeoConfig = {
  title: string;
  description: string;
  path: string;
  image?: string;
};

const pageSeo: Record<string, SeoConfig> = {
  '/': {
    title: 'Learn Social Media Marketing, Growth & Content Skills',
    description: 'Master TikTok, Instagram, and LinkedIn growth with proven content frameworks, viral hooks, analytics, and creator strategies.',
    path: '/',
    image: `${SITE_URL}/about-us-banner.jpg`,
  },
  '/pricing': {
    title: 'Affordable Social Media Learning Plans for Creators',
    description: 'Explore flexible pricing plans for creators. Learn viral content, social media strategy, video editing, and audience growth today.',
    path: '/pricing',
  },
  '/unsubscribe': {
    title: 'Cancel Subscription Plan | Learn Social AI',
    description: 'Request cancellation of your Learn Social AI subscription plan or manage your active plan preferences.',
    path: '/unsubscribe',
  },
  '/terms': {
    title: 'Terms and Conditions | Learn Social AI',
    description: 'Read the Terms and Conditions for Learn Social AI. Learn about your rights, responsibilities, and the rules governing use of our website and services.',
    path: '/terms',
  },
  '/privacy': {
    title: 'Privacy Policy | Learn Social AI',
    description: 'Review the Privacy Policy for Learn Social AI to understand how we collect, use, and protect your personal information when you use our website and services.',
    path: '/privacy',
  },
  '/signup': {
    title: 'Sign Up for Learn Social AI & Start Growing Today',
    description: 'Create your Learn Social AI account and access expert training in social media growth, viral content, analytics, and audience building.',
    path: '/signup',
  },
  '/imprint': {
    title: 'Imprint & Legal Information | Learn Social AI',
    description: "Access Learn Social AI's company registration details, contact information, legal disclosures, and website ownership information.",
    path: '/imprint',
  },
};

function setMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);

  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element?.setAttribute(key, value);
  });
}

function setCanonical(url: string) {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }

  element.setAttribute('href', url);
}

function setSchema(config: SeoConfig, url: string, image: string) {
  const existingSchema = document.head.querySelector<HTMLScriptElement>('script[data-seo-schema="true"]');
  existingSchema?.remove();

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: SITE_NAME,
        url: SITE_URL,
        logo: `${SITE_URL}/logo192.png`,
      },
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: SITE_URL,
        name: SITE_NAME,
        publisher: {
          '@id': `${SITE_URL}/#organization`,
        },
      },
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: config.title,
        description: config.description,
        image,
        isPartOf: {
          '@id': `${SITE_URL}/#website`,
        },
      },
    ],
  };

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-seo-schema', 'true');
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
}

export default function Seo() {
  const location = useLocation();

  useEffect(() => {
    const config = pageSeo[location.pathname] ?? pageSeo['/'];
    const url = `${SITE_URL}${config.path}`;
    const image = config.image ?? DEFAULT_IMAGE;

    document.title = config.title;
    setMeta('meta[name="description"]', { name: 'description', content: config.description });
    setMeta('meta[property="og:title"]', { property: 'og:title', content: config.title });
    setMeta('meta[property="og:description"]', { property: 'og:description', content: config.description });
    setMeta('meta[property="og:url"]', { property: 'og:url', content: url });
    setMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    setMeta('meta[property="og:image"]', { property: 'og:image', content: image });
    setCanonical(url);
    setSchema(config, url, image);
  }, [location.pathname]);

  return null;
}
